import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Popconfirm, Select, Input, Tag, Space, Row, Col, Pagination, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { AiOutlineSearch } from 'react-icons/ai';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import { SERVER_IP } from 'assets/Config';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import TableComponent from 'components/table-component';
import { formQueryStringFromObject } from 'helpers';
import { useI18n } from 'hooks/useI18n';
import {
	getWorkoutGenderOptions,
	getWorkoutLevelOptions,
	getWorkoutMuscleGroupOptions,
	translateWorkoutGender,
	translateWorkoutLevel,
	translateWorkoutMuscleGroup,
} from 'helpers/workoutI18n';

import './index.scss';

const GENDER_TAG_CLASS = {
	Male: 'workouts-tag--male',
	Female: 'workouts-tag--female',
	Unisex: 'workouts-tag--unisex',
};

const LEVEL_TAG_CLASS = {
	Beginner: 'workouts-tag--beginner',
	Intermediate: 'workouts-tag--intermediate',
	Advanced: 'workouts-tag--advanced',
};

const WorkoutsList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutsRedux = useSelector((state) => state.workoutsRedux);

	const [searchKey, setSearchKey] = useState('');
	const [genderFilter, setGenderFilter] = useState('');
	const [levelFilter, setLevelFilter] = useState('');
	const [muscleFilter, setMuscleFilter] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 10;
	const genderOptions = useMemo(() => getWorkoutGenderOptions(t, 'workouts.enums.gender.all_genders'), [t]);
	const levelOptions = useMemo(() => getWorkoutLevelOptions(t, 'workouts.enums.level.all_levels'), [t]);
	const muscleGroupOptions = useMemo(() => getWorkoutMuscleGroupOptions(t, 'workouts.enums.muscle_group.all_muscle_groups'), [t]);

	const tableData = Array.isArray(workoutsRedux?.list) ? workoutsRedux.list : [];

	const fetchWorkouts = useCallback(() => {
		const params = formQueryStringFromObject({
			branchId: globalRedux?.selectedBranch?.id,
		});
		dispatch(getApi('GET_WORKOUTS', `${SERVER_IP}api/workouts?${params}`));
	}, [dispatch, globalRedux?.selectedBranch?.id]);

	useEffect(() => {
		fetchWorkouts();
	}, [fetchWorkouts]);

	useEffect(() => {
		if (globalRedux.apiStatus.DELETE_WORKOUT === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_WORKOUT'));
			fetchWorkouts();
		}
	}, [globalRedux.apiStatus.DELETE_WORKOUT, dispatch, fetchWorkouts]);

	const handleDelete = (record) => {
		dispatch(deleteApi('DELETE_WORKOUT', `${SERVER_IP}api/workouts/${record.id}`));
	};

	const filteredData = useMemo(() => {
		let data = tableData;
		if (genderFilter) data = data.filter((w) => w.gender === genderFilter);
		if (levelFilter) data = data.filter((w) => w.level === levelFilter);
		if (muscleFilter) data = data.filter((w) => w.muscleGroup === muscleFilter);
		if (searchKey) data = data.filter((w) => w.name?.toLowerCase().includes(searchKey.toLowerCase()));
		return data;
	}, [tableData, genderFilter, levelFilter, muscleFilter, searchKey]);

	const paginatedData = useMemo(
		() => filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
		[filteredData, currentPage]
	);

	const columns = [
		{
			title: t('workouts.list.columns.index'),
			render: (_, __, index) => index + 1,
			width: 50,
		},
		{
			title: t('workouts.list.columns.name'),
			dataIndex: 'name',
			key: 'name',
			sorter: (a, b) => a.name?.localeCompare(b.name),
			render: (value, record) => (
				<div className="workouts-name-cell">
					<span className="workouts-name-cell__title">{value}</span>
					<span className="workouts-name-cell__meta">{record.instructions ? t('workouts.list.exercise_setup_available') : t('workouts.list.standard_exercise')}</span>
				</div>
			),
		},
		{
			title: t('workouts.list.columns.muscle_group'),
			dataIndex: 'muscleGroup',
			key: 'muscleGroup',
			render: (value) => <Tag className="workouts-tag workouts-tag--neutral">{translateWorkoutMuscleGroup(t, value, t('workouts.common.not_set'))}</Tag>,
		},
		{
			title: t('workouts.list.columns.equipment'),
			dataIndex: 'equipment',
			key: 'equipment',
			render: (value) => <Tag className="workouts-tag workouts-tag--equipment">{value || t('workouts.list.bodyweight')}</Tag>,
		},
		{
			title: t('workouts.list.columns.gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (value) => <Tag className={`workouts-tag ${GENDER_TAG_CLASS[value] || 'workouts-tag--neutral'}`}>{translateWorkoutGender(t, value, t('workouts.enums.gender.all'))}</Tag>,
		},
		{
			title: t('workouts.list.columns.level'),
			dataIndex: 'level',
			key: 'level',
			render: (value) => <Tag className={`workouts-tag ${LEVEL_TAG_CLASS[value] || 'workouts-tag--neutral'}`}>{translateWorkoutLevel(t, value, t('workouts.enums.level.general'))}</Tag>,
		},
		{
			title: t('workouts.list.columns.sets_reps'),
			key: 'setsReps',
			render: (_, record) => <span className="workouts-volume-pill">{record.defaultSets} × {record.defaultReps}</span>,
		},
		{
			title: t('common.actions'),
			key: 'actions',
			align: 'center',
			render: (_, record) => (
				<div className="customer-action-group workouts-action-group">
					<Tooltip title={t('workouts.list.view_exercise')}>
						<Button
							type="text"
							className="customer-action-btn customer-action-btn--workout"
							icon={<EyeOutlined />}
							onClick={() => navigate(`/workouts/${record.id}`)}
							aria-label={t('workouts.list.view_exercise')}
						/>
					</Tooltip>
					<Tooltip title={t('workouts.list.edit_exercise')}>
						<Button
							type="text"
							className="customer-action-btn customer-action-btn--edit"
							icon={<EditOutlined />}
							onClick={() => navigate(`/workouts/edit/${record.id}`)}
							aria-label={t('workouts.list.edit_exercise')}
						/>
					</Tooltip>
					<Popconfirm
						title={t('workouts.list.delete_confirm')}
						onConfirm={() => handleDelete(record)}
						okText={t('common.yes')}
						cancelText={t('common.no')}
					>
						<Tooltip title={t('workouts.list.delete_exercise')}>
							<Button
								type="text"
								className="customer-action-btn customer-action-btn--delete"
								icon={<DeleteOutlined />}
								aria-label={t('workouts.list.delete_exercise')}
							/>
						</Tooltip>
					</Popconfirm>
				</div>
			),
		},
	];

	return (
		<Row className="workouts-page">
			<Col span={24} className="workouts-page__content">
				<TableComponent
					loading={globalRedux.apiStatus.GET_WORKOUTS === 'PENDING'}
					className="custom-table workouts-table"
					style={{ width: '100%' }}
					columns={columns}
					bordered
					rowKey="id"
					dataSource={paginatedData}
					title={() => (
							<div className="workouts-toolbar">
								<div className="workouts-toolbar__copy">
									<span className="workouts-toolbar__eyebrow">{t('workouts.list.eyebrow')}</span>
									<h3 className="workouts-toolbar__title">{t('workouts.list.title')}</h3>
									<p className="workouts-toolbar__subtitle">{t('workouts.list.subtitle')}</p>
								</div>
								<Row className="workouts-toolbar__controls" style={{ justifyContent: 'space-between' }} align="middle" gutter={[12, 12]}>
									<Col flex="auto">
										<Row gutter={[10, 10]} align="middle">
											<Col>
												<Input
													className="workouts-search"
													placeholder={t('workouts.list.search_placeholder')}
													suffix={<AiOutlineSearch />}
													value={searchKey}
													allowClear
													onChange={(e) => { setSearchKey(e.target.value); setCurrentPage(1); }}
												/>
											</Col>
											<Col>
												<Select
													className="workouts-filter workouts-filter--compact"
													options={genderOptions}
													value={genderFilter}
													onChange={(v) => { setGenderFilter(v); setCurrentPage(1); }}
												/>
											</Col>
											<Col>
												<Select
													className="workouts-filter workouts-filter--compact"
													options={levelOptions}
													value={levelFilter}
													onChange={(v) => { setLevelFilter(v); setCurrentPage(1); }}
												/>
											</Col>
											<Col>
												<Select
													className="workouts-filter workouts-filter--wide"
													options={muscleGroupOptions}
													value={muscleFilter}
													onChange={(v) => { setMuscleFilter(v); setCurrentPage(1); }}
												/>
											</Col>
											<Col>
												<div className="workouts-results-chip">
													<strong>{filteredData.length}</strong>
													<span>{filteredData.length === 1 ? t('workouts.list.exercise_one') : t('workouts.list.exercise_other')}</span>
												</div>
											</Col>
										</Row>
									</Col>
									<Col>
										<Space className="workouts-toolbar__actions" size={10}>
											<Button
												className="workouts-refresh-btn"
												icon={<ReloadOutlined />}
												loading={globalRedux.apiStatus.GET_WORKOUTS === 'PENDING'}
												onClick={fetchWorkouts}
											/>
											<Button
												className="workouts-add-btn"
												type="primary"
												icon={<PlusOutlined />}
												onClick={() => navigate('/workouts/add')}
											>
												{t('workouts.list.add_exercise')}
											</Button>
										</Space>
									</Col>
								</Row>
							</div>
					)}
					pagination={false}
					footer={() => (
						<Row justify="space-between" align="middle" className="workouts-footer">
							<Col span={12}>
								{filteredData.length > 0 &&
									<span className="workouts-footer__summary">{t('workouts.list.showing_summary', { start: (currentPage - 1) * pageSize + 1, end: Math.min(currentPage * pageSize, filteredData.length), total: filteredData.length })}</span>}
							</Col>
							<Col span={12}>
								<div className="workouts-footer__pagination">
									<Pagination
										pageSizeOptions={[10, 20, 50]}
										defaultPageSize={10}
										showSizeChanger
										total={filteredData.length}
										current={currentPage}
										onChange={(page) => setCurrentPage(page)}
										responsive
									/>
								</div>
							</Col>
						</Row>
					)}
				/>
			</Col>
		</Row>
	);
};

export default WorkoutsList;
